import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useI18n } from '../../context/I18nContext';
import Dot from '../Dot';
import './LoginBanner.css';

const loginPageImages = [
  {
    image: '/images/netsfereCorePillars/image1.png',
    alt: 'security',
    title: 'enablingCommunication',
    description: 'enablingDesc',
    textPosition: 'top',
  },
  {
    image: '/images/netsfereCorePillars/image2.png',
    alt: 'control',
    title: 'security',
    description: 'securityDesc',
    textPosition: 'bottom',
  },
  {
    image: '/images/netsfereCorePillars/image3.png',
    alt: 'compliance',
    title: 'control',
    description: 'controlDesc',
    textPosition: 'bottom',
  },
  {
    image: '/images/netsfereCorePillars/image4.png',
    alt: 'collaboration',
    title: 'compliance',
    description: 'complianceDesc',
    textPosition: 'bottom',
  },
  {
    image: '/images/netsfereCorePillars/image5.png',
    alt: 'innovation',
    title: 'collaboration',
    description: 'collaborationDesc',
    textPosition: 'bottom',
  },
];

const BannerSlide = ({ slideData, index, imgStyle }) => {
  const { t } = useI18n();

  const renderImageText = (titleText, descText, renderSide) => {
    const titleClass = `login-banner-title ${renderSide === 'top' ? 'login-banner-title--large' : 'login-banner-title--medium'}`;
    const wrapperClass = `login-banner-text-wrapper ${renderSide === 'top' ? 'login-banner-text-wrapper--top' : 'login-banner-text-wrapper--bottom'}`;

    return (
      <div className={wrapperClass}>
        <div className={titleClass}>{titleText}</div>
        <div className="login-banner-description">{descText}</div>
      </div>
    );
  };

  return (
    <div className="login-banner-slide" id={`slide-${index + 1}`}>
      <img 
        src={slideData.image} 
        alt={t(slideData.alt)} 
        className="login-banner-image" 
        style={imgStyle} 
      />
      {renderImageText(t(slideData.title), t(slideData.description), slideData.textPosition)}
    </div>
  );
};

BannerSlide.propTypes = {
  slideData: PropTypes.shape({
    image: PropTypes.string.isRequired,
    alt: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    textPosition: PropTypes.oneOf(['top', 'bottom']).isRequired,
  }).isRequired,
  index: PropTypes.number.isRequired,
  imgStyle: PropTypes.object.isRequired,
};

const LoginBanner = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [translateValue, setTranslateValue] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const imageSliderInterval = useRef(null);
  const carouselSlideRef = useRef(null);

  const getSlideWidth = () => {
    return carouselSlideRef.current ? carouselSlideRef.current.clientWidth : 0;
  };

  const startInterval = () => {
    if (!isPaused && imageSliderInterval.current === null) {
      imageSliderInterval.current = setInterval(goToSlide, 4000);
    }
  };

  const goToSlide = () => {
    setCurrentIndex(prevIndex => {
      const nextIndex = prevIndex === loginPageImages.length - 1 ? 0 : prevIndex + 1;
      setTranslateValue(-(getSlideWidth() * nextIndex));
      return nextIndex;
    });
  };

  const clearImageSliderInterval = () => {
    if (imageSliderInterval.current !== null) {
      clearInterval(imageSliderInterval.current);
      imageSliderInterval.current = null;
    }
  };

  // Set up the auto-slide interval
  useEffect(() => {
    startInterval();

    return () => {
      clearImageSliderInterval();
    };
  }, [isPaused]); // Restart interval when pause state changes

  const handleDotClick = (index) => {
    // Clear existing interval and restart it to reset the timer
    clearImageSliderInterval();
    
    setCurrentIndex(index);
    setTranslateValue(-(getSlideWidth() * index));
    
    // Restart the interval
    startInterval();
  };

  const handleMouseEnter = () => {
    setIsPaused(true);
    clearImageSliderInterval();
  };

  const handleMouseLeave = () => {
    setIsPaused(false);
  };

  // Dynamic styles that need to be computed at runtime
  const carouselSlideStyle = {
    transform: `translateX(${translateValue}px)`,
  };

  const imgStyle = {
    height: window.innerHeight,
  };

  return (
    <div 
      className="login-banner"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="login-banner-carousel-container">
        <div 
          className="login-banner-carousel-slide" 
          style={carouselSlideStyle}
          ref={carouselSlideRef}
        >
          {loginPageImages.map((slideData, index) => (
            <BannerSlide
              key={index}
              slideData={slideData}
              index={index}
              imgStyle={imgStyle}
            />
          ))}
        </div>
      </div>
      <div className="login-banner-dots">
        {
          loginPageImages.map((slideData, index) => (
            <Dot
              key={index}
              dotIndex={index}
              dotSize={6}
              currentIndex={currentIndex}
              onDotClick={handleDotClick}
            />
          ))
        }
      </div>
    </div>
  );
};

export default LoginBanner;
